import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Menu
} from '@mui/material';
import TeamWhiteButtonWithIcon from 'team/shared/components/button/TeamWhiteButtonWithIcon';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const TeamMembersSortModal = ({ open, onClose, currentSort, onApplySort, anchorEl }) => {
    const [selectedSort, setSelectedSort] = useState(currentSort || '');

    // Initialize when modal opens
    useEffect(() => {
        if (open) {
            setSelectedSort(currentSort || '');
        }
    }, [open, currentSort]);

    const handleChange = (event) => {
        setSelectedSort(event.target.value);
    };

    const handleClearSort = () => {
        setSelectedSort('');
        if (onApplySort) {
            onApplySort('');
        }
        onClose();
    };

    const handleApplySort = () => {
        if (onApplySort) {
            onApplySort(selectedSort);
        }
        onClose();
    };

    // Check if there's a selection and if it's changed (for button states)
    const hasSelection = selectedSort !== '';
    const hasChanged = selectedSort !== (currentSort || '');
    const hasCurrentSort = (currentSort || '') !== '';

    // Sort options
    const sortOptions = [
        { value: 'dateNewest', label: 'Date Added (Newest', labelEnd: 'Oldest)' },
        { value: 'dateOldest', label: 'Date Added (Oldest', labelEnd: 'Newest)' },
        { value: 'alphabeticalAZ', label: 'Alphabetical (A', labelEnd: 'Z)' },
        { value: 'alphabeticalZA', label: 'Alphabetical (Z', labelEnd: 'A)' }
    ];

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            disableAutoFocusItem
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
            }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    width: {
                        xs: '70vw',
                        sm: '280px',
                        md: '300px'
                    },
                    maxWidth: '300px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#fafafa'
                }
            }}
            MenuListProps={{
                sx: {
                    padding: 0
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        cursor: 'default'
                    }
                }
            }}
        >
            <Box sx={{ padding: 1.5 }}>
                <Typography variant="h6" fontWeight="600" sx={{ mb: 1.5, fontSize: '1rem' }}>
                    Sort By
                </Typography>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 1.5 }}>
                    <RadioGroup
                        aria-label="sort-options"
                        name="sort-options"
                        value={selectedSort}
                        onChange={handleChange}
                    >
                        {sortOptions.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio />}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.9rem' }}>
                                            {option.label}
                                        </Typography>
                                        <ArrowForwardIcon sx={{ fontSize: '1rem', color: '#828282', mx: 0.5 }} />
                                        <Typography sx={{ fontSize: '0.9rem' }}>
                                            {option.labelEnd}
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    marginY: 0.5,
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <TeamWhiteButtonWithIcon
                        text="Clear Sort"
                        onClick={handleClearSort}
                        disabled={!hasCurrentSort}
                        padding="6px 16px"
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            color: hasCurrentSort ? '#4f4f4f' : '#828282',
                            border: hasCurrentSort ? '0.0625rem solid #e3e3e3' : 'none',
                            '&:hover': {
                                backgroundColor: hasCurrentSort ? 'white' : 'white',
                                boxShadow: hasCurrentSort ? 2 : 'none',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: 'white',
                                color: '#828282',
                                border: 'none'
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleApplySort}
                        disabled={!hasChanged || !hasSelection}
                        sx={{
                            backgroundColor: (hasChanged && hasSelection) ? '#000' : '#e0e0e0',
                            color: (hasChanged && hasSelection) ? '#fff' : '#a6a6a6',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            padding: '6px 16px',
                            '&:hover': {
                                backgroundColor: (hasChanged && hasSelection) ? '#333' : '#e0e0e0',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#a6a6a6'
                            }
                        }}
                    >
                        Apply Sort
                    </Button>
                </Box>
            </Box>
        </Menu>
    );
};

export default TeamMembersSortModal;

